import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiClient, User, Organization } from '../lib/api-client';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  organization: Organization | null;
  error: string | null;
}

type AuthAction = 
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; organization?: Organization } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'UPDATE_ORGANIZATION'; payload: Organization }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  organization: null,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_LOADING':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        organization: action.payload.organization || state.organization,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        organization: null,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        organization: null,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'UPDATE_ORGANIZATION':
      return {
        ...state,
        organization: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    organizationName: string;
  }) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  refreshOrganization: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    dispatch({ type: 'AUTH_LOADING' });

    try {
      // Check if we have a stored token
      const token = localStorage.getItem('auth-token');
      console.log('InitializeAuth - Token found:', !!token, token?.substring(0, 20) + '...');
      
      if (!token) {
        // No token is normal for new users - don't show error
        console.log('InitializeAuth - No token found, logging out');
        dispatch({ type: 'AUTH_LOGOUT' });
        return;
      }

      // Validate the token by getting current user
      console.log('InitializeAuth - Validating token with /auth/me');
      const userResponse = await apiClient.getCurrentUser();
      console.log('InitializeAuth - User response:', userResponse.success, userResponse.error);
      
      if (!userResponse.success || !userResponse.data) {
        throw new Error(userResponse.error || 'Failed to get user data');
      }

      // Get organization data
      console.log('InitializeAuth - Getting organization data');
      const orgResponse = await apiClient.getOrganization();
      console.log('InitializeAuth - Organization response:', orgResponse.success, orgResponse.error);
      const organization = orgResponse.success ? orgResponse.data : undefined;

      console.log('InitializeAuth - Success! User:', userResponse.data.email);
      dispatch({ 
        type: 'AUTH_SUCCESS', 
        payload: { 
          user: userResponse.data,
          organization 
        } 
      });
    } catch (error) {
      console.error('Auth initialization failed:', error);
      // Clear invalid token
      localStorage.removeItem('auth-token');
      // Don't show error for failed token validation - just log out silently
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    dispatch({ type: 'AUTH_LOADING' });

    try {
      const response = await apiClient.login(email, password);
      
      if (!response.success || !response.data) {
        const errorMessage = response.error || 'Invalid email or password. Please try again.';
        dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
        return false;
      }

      // Get organization data
      const orgResponse = await apiClient.getOrganization();
      const organization = orgResponse.success ? orgResponse.data : undefined;

      dispatch({ 
        type: 'AUTH_SUCCESS', 
        payload: { 
          user: response.data.user,
          organization 
        } 
      });
      return true;
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ type: 'AUTH_ERROR', payload: 'Network error. Please check your connection and try again.' });
      return false;
    }
  };

  const register = async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    organizationName: string;
  }): Promise<boolean> => {
    dispatch({ type: 'AUTH_LOADING' });

    try {
      const response = await apiClient.register(userData);
      
      if (!response.success || !response.data) {
        const errorMessage = response.error || 'Registration failed. Please check your information and try again.';
        dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
        return false;
      }

      // Get organization data
      const orgResponse = await apiClient.getOrganization();
      const organization = orgResponse.success ? orgResponse.data : undefined;

      dispatch({ 
        type: 'AUTH_SUCCESS', 
        payload: { 
          user: response.data.user,
          organization 
        } 
      });
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      dispatch({ type: 'AUTH_ERROR', payload: 'Network error. Please check your connection and try again.' });
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const response = await apiClient.getCurrentUser();
      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_USER', payload: response.data });
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const refreshOrganization = async (): Promise<void> => {
    try {
      const response = await apiClient.getOrganization();
      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_ORGANIZATION', payload: response.data });
      }
    } catch (error) {
      console.error('Failed to refresh organization data:', error);
    }
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    refreshUser,
    refreshOrganization,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Higher-order component for protecting routes
export function withAuth<P extends object>(Component: React.ComponentType<P>) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login or show login form
      window.location.href = '/login';
      return null;
    }

    return <Component {...props} />;
  };
}

// Hook for requiring specific roles
export function useRequireRole(allowedRoles: User['role'][]) {
  const { user, isAuthenticated } = useAuth();
  
  const hasRole = isAuthenticated && user && allowedRoles.includes(user.role);
  
  return {
    hasRole,
    userRole: user?.role,
    isLoading: !isAuthenticated,
  };
}
